import { getCurrentScope, isError, withScope } from '@sentry/core';
import {
  captureException,
  captureMessage,
  captureReactException,
  type SeverityLevel,
} from '@sentry/react';
import type { ErrorInfo } from 'react';

import { SilencedError, isSilencedError } from '@sorare/error-boundary';

import { isNoInternetConnectionError } from './http/isNoInternetConnectionError.ts';

type WithMessage = {
  message: string;
};

type WithCode = {
  code: number | string;
};

const hasMessage = (err: unknown): err is WithMessage => {
  if (!(err as any)?.message) {
    return false;
  }
  return typeof (err as any).message === 'string';
};

const hasCode = (err: unknown): err is WithCode => {
  if (!(err as any)?.code) {
    return false;
  }
  const { code } = err as any;
  return typeof code === 'string' || typeof code === 'number';
};

const buildMessage = (err: WithMessage): string => {
  if (hasCode(err)) {
    return `${err.code}: ${err.message}`;
  }
  return err.message;
};

// Catch-all captures below share an identical message and no app stack frame,
// so Sentry collapses every distinct root cause into one un-actionable issue.
// The explicit fingerprints split them by route (and thrown-value shape) so the
// mega-issue breaks into per-surface groups that can be triaged.
const getCurrentRoute = (): string => {
  const { transactionName } = getCurrentScope().getScopeData();
  if (transactionName) {
    return transactionName;
  }
  // oxlint-disable-next-line no-restricted-properties
  return window.location?.pathname || 'unknown';
};

export const describeThrownValue = (err: unknown): string => {
  if (err === null) {
    return 'null';
  }
  if (err === undefined) {
    return 'undefined';
  }
  const type = typeof err;
  if (type !== 'object') {
    return `${type}: ${String(err).slice(0, 50)}`;
  }
  const obj = err as Record<string, unknown>;
  const constructorName =
    (obj.constructor as { name?: string } | undefined)?.name || 'Object';
  const named = [obj.name, obj.type, obj.reason].find(
    (v): v is string => typeof v === 'string' && v.length > 0
  );
  if (named) {
    return `${constructorName}: ${named}`;
  }
  const keys = Object.keys(obj).sort().slice(0, 5).join(',');
  return keys ? `${constructorName} {${keys}}` : constructorName;
};

const captureMsgException = (err: { [key: string]: string }) => {
  const exception = new Error();
  if ('msg' in err) {
    exception.message = err.msg;
  }
  if ('line' in err && 'url' in err && 'column' in err) {
    exception.stack = `Error: ${err.msg}\n    at ${err.url}:${err.line}:${err.column}`;
  }
  const route = getCurrentRoute();
  withScope(scope => {
    scope.setExtra('full_object', {
      err,
    });
    scope.setTag('caught_error.route', route);
    scope.setFingerprint(['caught-msg', err.msg || 'unknown', route]);
    captureException(exception);
  });
};

export const sendSafeError = (err: unknown) => {
  if (isSilencedError(err)) {
    return;
  }
  if (isError(err)) {
    captureException(err);
    return;
  }
  if (hasMessage(err)) {
    captureMessage(buildMessage(err), 'error' as SeverityLevel);
  }
  // starting from react 19, we start to have these obscure objects that are not thrown
  if (typeof err === 'object' && err !== null && 'msg' in err) {
    captureMsgException(err as { [key: string]: string });
    return;
  }
  const description = describeThrownValue(err);
  const route = getCurrentRoute();
  withScope(scope => {
    scope.setExtra('full_object', {
      err,
    });
    scope.setTag('caught_error.route', route);
    scope.setFingerprint(['unhandled-thrown', description, route]);
    captureException(new Error(`Unhandled object thrown: ${description}`));
  });
};

const wrapHandledError = (
  code: number | string,
  message?: string,
  urls?: { label: string; url: string }[]
) => {
  return {
    handleError: true,
    error: { code, message, urls },
  };
};

export const formatError = (error?: any) => {
  if (!error) return null;
  error.statusCode ||= error.status;

  const { graphQLErrors } = error || {};
  const graphqlErrorCode = graphQLErrors?.[0]?.extensions?.code;

  if (error instanceof SilencedError) {
    return wrapHandledError('OOPS', error.message);
  }
  if (error.statusCode === 401) {
    return wrapHandledError(error.statusCode, 'Unauthorized.');
  }
  if (error.statusCode === 404 || graphqlErrorCode === 'NOT_FOUND') {
    return wrapHandledError(error.statusCode, 'Page not found.');
  }
  if (error.statusCode === 429) {
    return wrapHandledError(
      error.statusCode,
      'Too many requests, try again later.'
    );
  }
  if (error.statusCode === 503) {
    return wrapHandledError(
      error.statusCode,
      'Maintenance operation in progress. Please retry later.'
    );
  }
  if (error.statusCode) {
    return wrapHandledError(
      error.statusCode,
      `Something went wrong! (${error.statusCode})`
    );
  }
  if (error.networkError) {
    if (isNoInternetConnectionError(error.networkError)) {
      if (import.meta.env.MODE === 'development') {
        return wrapHandledError(
          'OOPS',
          `Couldn't connect to the Sorare API: ${error.message}.`
        );
      }
      return wrapHandledError('OOPS', 'Lost internet connection.');
    }
    if (error.networkError instanceof TypeError) {
      // this happens when the API send invalid JSON
      // TypeError: Failed to execute 'text' on 'Response': body stream already read.
      if (import.meta.env.MODE === 'development') {
        return wrapHandledError(
          'OOPS',
          error.message || error.networkError.message
        );
      }
      return wrapHandledError('OOPS', 'Internal server error.');
    }
    return wrapHandledError('OOPS', `Unexpected error: ${error.networkError}.`);
  }
  return null;
};

export function reactErrorHandler(
  tag: 'caught' | 'uncaught' | 'recoverable'
): (error: any, errorInfo: ErrorInfo) => void {
  return (rawError: any, errorInfo: ErrorInfo) => {
    // React recovered on its own: only the underlying cause it carries is actionable.
    if (tag === 'recoverable' && !(rawError?.cause instanceof Error)) {
      return;
    }
    const error = tag === 'recoverable' ? rawError.cause : rawError;

    const { handleError } = formatError(error) || {};
    if (handleError) {
      return;
    }
    if (import.meta.env.MODE === 'development') {
      // eslint-disable-next-line no-console
      console.error(error, errorInfo);
    }
    captureReactException(error, errorInfo || {}, {
      mechanism: {
        handled: false,
        type: `auto.function.sentry.error_handler.${tag}`,
      },
    });
  };
}
